import type React from 'react';

import * as api from '@gitbook/api-client';
import {
    Alert,
    Button,
    Dialog,
    DialogBody,
    DialogCloseButton,
    DialogFooter,
    DialogHeader,
    Heading,
    Link,
} from '@gitbook/spine-react';

import { type GitSyncCurrentStatus, GitSyncProviderName } from '../../../Components';
import { DocumentationLink } from '../../../Components/Link';
import { useContactSupport } from '../../../state';
import { createDocumentationURL } from '../../../utils';

/**
 * Dialog to display when a Git synchronization on a space has failed.
 */
export function SpaceGitSyncErrorDialog(props: {
    space: api.Space;
    gitSyncStatus: GitSyncCurrentStatus | null;
}): React.ReactElement {
    const { space, gitSyncStatus } = props;

    const contactSupport = useContactSupport();

    const provider = GitSyncProviderName[space.gitSync?.installationProvider ?? 'github'];

    return (
        <Dialog>
            <DialogHeader>
                <Heading>Git Sync failure</Heading>
            </DialogHeader>
            <DialogBody>
                {gitSyncStatus?.state === 'timed-out' ? (
                    <div className="flex flex-col">
                        <div className="mt-2">
                            A sync operation with {provider} has <strong>timed out.</strong> This
                            can happen when the size of the content being synchronized is too large
                            or if there is a network connectivity issue.
                        </div>
                        <div className="mt-4">
                            You can try to retry the sync or{' '}
                            <Link onPress={() => contactSupport()}>contact support</Link> if the
                            problem persists.
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col">
                        <div className="mt-2">
                            {gitSyncStatus?.direction === api.GitSyncOperationDirection.Import
                                ? `An error was detected while importing recent commits from ${provider}, to resume synchronization & merging of change requests please fix the error.`
                                : `An error was detected while exporting the latest changes to ${provider}. It is recommended to fix the error before merging further change requests.`}
                        </div>
                        <Alert
                            kind="danger"
                            details={gitSyncStatus?.error ?? 'Unknown error'}
                            className="mt-4"
                        />
                        <div className="mt-4">
                            Refer to our{' '}
                            <DocumentationLink path="product-tour/git-sync/troubleshooting">
                                troubleshooting guide
                            </DocumentationLink>{' '}
                            for more information or{' '}
                            <Link onPress={() => contactSupport()}>contact support</Link> if the
                            problem persists.
                        </div>
                    </div>
                )}
            </DialogBody>
            <DialogFooter>
                <DialogCloseButton label="Close" />
                {gitSyncStatus?.state === 'failure' ? (
                    <Button
                        label="Troubleshooting guide"
                        kind="primary"
                        href={createDocumentationURL('product-tour/git-sync/troubleshooting')}
                    />
                ) : null}
            </DialogFooter>
        </Dialog>
    );
}
